import * as React from 'react'
import { Input, Card, CardBody, FormGroup, Label } from 'reactstrap'
import { DropdownComponent, DropdownOption, OnChangeEvent } from 'components/dropdown/component'
import * as Modals from 'shared/modals'
import * as RexConstants from '../../rex/constants'
import { TRexIcon } from 'components/icons/t-rex'
import './styles.scss'

interface Props extends Modals.ModalComponentProps {
  name: string
  options: {
    tone: string
    length: number
    language: string
    emoji: number
    format: string
    complexity: string
    perspective: string
    creativity: string
  }
  isDefault: boolean
  createOrUpdate: (payload: any) => Promise<any>
}

interface State extends Modals.ModalComponentState {
  name: string
  tone: DropdownOption
  length: DropdownOption
  language: DropdownOption
  emoji: DropdownOption
  format: DropdownOption
  complexity: DropdownOption
  perspective: DropdownOption
  creativity: DropdownOption
  isDefault: boolean
  error: string
}

const emptyState: State = {
  name: '',
  tone: RexConstants.ToneDropdownOptions[0], // Neutral
  length: RexConstants.LengthDropdownOptions[1], // Medium
  language: RexConstants.LanguageDropdownOptions[0], // English
  emoji: RexConstants.EmojiDropdownOptions[0], // No Emojis
  format: RexConstants.FormatTypeOptions[0], // Text
  complexity: RexConstants.ComplexityDropdownOptions[1], // Intermediate
  perspective: RexConstants.PerspectiveDropdownOptions[0], // Neutral
  creativity: RexConstants.CreativityDropdownOptions[0], // Neutral
  isDefault: false,
  error: '',
}

export class AddAIPreferenceModal extends Modals.GenericModalComponent<Props, State> {
  public static key: string = 'addAIPreferenceModal'

  public static defaultProps = {
    closeButtonText: 'Cancel',
    saveText: 'Save Preference',
    secondAction: Modals.ModalActionType.BUTTON,
  }

  constructor(props: Props) {
    super(props, AddAIPreferenceModal.key)
    this.state = emptyState
  }

  public componentDidMount() {
    this.loadFormData()
  }

  public componentDidUpdate(prevProps: Props) {
    // If props changed (modal opened with new data), reload form
    if (prevProps.name !== this.props.name || prevProps.options !== this.props.options) {
      this.loadFormData()
    }
  }

  private loadFormData = () => {
    // If editing existing preference, populate form
    if (this.props.name) {
      const toneOption = RexConstants.ToneDropdownOptions.find(t => t.value === (this.props.options && this.props.options.tone)) || RexConstants.ToneDropdownOptions[0]
      const lengthOption = RexConstants.LengthDropdownOptions.find(l => l.value === (this.props.options && this.props.options.length)) || RexConstants.LengthDropdownOptions[1]
      const languageOption = RexConstants.LanguageDropdownOptions.find(l => l.value === (this.props.options && this.props.options.language)) || RexConstants.LanguageDropdownOptions[0]
      const emojiOption = RexConstants.EmojiDropdownOptions.find(e => e.value === (this.props.options && this.props.options.emoji)) || RexConstants.EmojiDropdownOptions[0]
      const formatOption = RexConstants.FormatTypeOptions.find(f => f.value === (this.props.options && this.props.options.format)) || RexConstants.FormatTypeOptions[0]
      const complexityOption = RexConstants.ComplexityDropdownOptions.find(c => c.value === (this.props.options && this.props.options.complexity)) || RexConstants.ComplexityDropdownOptions[1]
      const perspectiveOption = RexConstants.PerspectiveDropdownOptions.find(p => p.value === (this.props.options && this.props.options.perspective)) || RexConstants.PerspectiveDropdownOptions[0]
      const creativityOption = RexConstants.CreativityDropdownOptions.find(c => c.value === (this.props.options && this.props.options.creativity)) || RexConstants.CreativityDropdownOptions[0]

      this.setState({
        name: this.props.name,
        tone: toneOption,
        length: lengthOption,
        language: languageOption,
        emoji: emojiOption,
        format: formatOption,
        complexity: complexityOption,
        perspective: perspectiveOption,
        creativity: creativityOption,
        isDefault: this.props.isDefault || false,
      })
    } else {
      // Reset to empty state for new preference
      this.setState(emptyState)
    }
  }

  protected getTitleText(data: any): string {
    return this.props.name ? 'Edit AI Preference' : 'Add New AI Preference'
  }

  protected xButtonClick(data: any) {
    this.clearState()
    super.xButtonClick(data)
  }

  protected close(data: any) {
    this.clearState()
    super.close(data)
  }

  protected save(data: any) {
    if (!this.state.name.trim()) {
      this.setState({ error: 'Please enter a preference name.' })
      return
    }

    const payload = {
      name: this.state.name.trim(),
      options: {
        tone: this.state.tone.value,
        length: this.state.length.value,
        language: this.state.language.value,
        emoji: this.state.emoji.value,
        format: this.state.format.value,
        complexity: this.state.complexity.value,
        perspective: this.state.perspective.value,
        creativity: this.state.creativity.value,
      },
      is_default: this.state.isDefault,
    }

    this.props.createOrUpdate(payload)
      .then(() => {
        this.clearState()
        super.close(data)
      })
      .catch((error) => {
        this.setState({ error: 'Failed to save preference. Please try again.' })
      })
  }

  protected saveDisabled(data: any): boolean {
    return !this.state.name.trim()
  }

  private clearState = () => {
    this.setState(emptyState)
  }

  private setName = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ name: event.target.value, error: '' })
  }

  private setTone = (event: OnChangeEvent) => {
    this.setState({ tone: event.selected })
  }

  private setLength = (event: OnChangeEvent) => {
    this.setState({ length: event.selected })
  }

  private setLanguage = (event: OnChangeEvent) => {
    this.setState({ language: event.selected })
  }

  private setEmoji = (event: OnChangeEvent) => {
    this.setState({ emoji: event.selected })
  }

  private setFormat = (event: OnChangeEvent) => {
    this.setState({ format: event.selected })
  }

  private setComplexity = (event: OnChangeEvent) => {
    this.setState({ complexity: event.selected })
  }

  private setPerspective = (event: OnChangeEvent) => {
    this.setState({ perspective: event.selected })
  }

  private setCreativity = (event: OnChangeEvent) => {
    this.setState({ creativity: event.selected })
  }

  private setIsDefault = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ isDefault: event.target.checked })
  }

  private generatePreviewText = (): string => {
    const parts = []
    
    if (this.state.tone && this.state.tone.key && this.state.tone.key !== 'Neutral') {
      parts.push(`${String(this.state.tone.key).toLowerCase()} tone`)
    }
    
    if (this.state.length && this.state.length.key) {
      parts.push(`${String(this.state.length.key).toLowerCase()} length`)
    }
    
    if (this.state.format && this.state.format.key && this.state.format.key !== 'Text') {
      parts.push(`${String(this.state.format.key).toLowerCase()} format`)
    }
    
    if (this.state.complexity && this.state.complexity.key && this.state.complexity.key !== 'Intermediate') {
      parts.push(`${String(this.state.complexity.key).toLowerCase()} complexity`)
    }
    
    if (this.state.perspective && this.state.perspective.key && this.state.perspective.key !== 'Neutral') {
      parts.push(`${String(this.state.perspective.key).toLowerCase()} perspective`)
    }
    
    if (this.state.creativity && this.state.creativity.key && this.state.creativity.key !== 'Neutral') {
      parts.push(`${String(this.state.creativity.key).toLowerCase()} creativity`)
    }
    
    if (this.state.language && this.state.language.key && this.state.language.key !== 'English') {
      parts.push(`in ${this.state.language.key}`)
    }
    
    const emojiText = (this.state.emoji && this.state.emoji.value > 1) ? 'with multiple emojis' : 
                     (this.state.emoji && this.state.emoji.value === 1) ? 'with occasional emojis' : 'without emojis'
    parts.push(emojiText)
    
    return parts.length > 1 ? `Generate text with ${parts.join(', ')}.` : 'Generate text with default settings.'
  }

  protected renderChildren(data: any) {
    return (
      <div className="ai-image-preference-modal">
        <div className="ai-image-preference-form">
          {this.state.error && (
            <div className="alert alert-danger" role="alert">
              {this.state.error}
            </div>
          )}

          <FormGroup>
            <Label className="ai-image-preference-label">Preference Name</Label>
            <Input
              type="text"
              value={this.state.name}
              onChange={this.setName}
              placeholder="Enter a name for this preference"
              className="ai-image-preference-input"
            />
          </FormGroup>

          <Card className="ai-image-preference-settings-card">
            <CardBody>
              <div className="ai-image-options-header mb-3 text-center">
                <div className="d-flex align-items-center justify-content-center">
                  <TRexIcon svgwidth="24" svgheight="24" className="mr-2" />
                  <h6 className="ai-image-preference-settings-title mb-0">AI Text Generation Settings</h6>
                </div>
                <p className="text-muted mb-0 mt-1">Configure how AI generates text content for this preference</p>
              </div>

              <div className="ai-image-preference-settings-grid">
                <div className="row">
                  <div className="col-md-6">
                    <FormGroup>
                      <Label className="ai-image-preference-option-label">Tone</Label>
                      <DropdownComponent
                        data={RexConstants.ToneDropdownOptions}
                        selected={this.state.tone.key}
                        onChange={this.setTone}
                        className="ai-image-preference-dropdown"
                      />
                    </FormGroup>
                  </div>

                  <div className="col-md-6">
                    <FormGroup>
                      <Label className="ai-image-preference-option-label">Length</Label>
                      <DropdownComponent
                        data={RexConstants.LengthDropdownOptions}
                        selected={this.state.length.key}
                        onChange={this.setLength}
                        className="ai-image-preference-dropdown"
                      />
                    </FormGroup>
                  </div>

                  <div className="col-md-6">
                    <FormGroup>
                      <Label className="ai-image-preference-option-label">Language</Label>
                      <DropdownComponent
                        data={RexConstants.LanguageDropdownOptions}
                        selected={this.state.language.key}
                        onChange={this.setLanguage}
                        className="ai-image-preference-dropdown"
                      />
                    </FormGroup>
                  </div>

                  <div className="col-md-6">
                    <FormGroup>
                      <Label className="ai-image-preference-option-label">Include Emojis</Label>
                      <DropdownComponent
                        data={RexConstants.EmojiDropdownOptions}
                        selected={this.state.emoji.key}
                        onChange={this.setEmoji}
                        className="ai-image-preference-dropdown"
                      />
                    </FormGroup>
                  </div>

                  <div className="col-md-6">
                    <FormGroup>
                      <Label className="ai-image-preference-option-label">Format</Label>
                      <DropdownComponent
                        data={RexConstants.FormatTypeOptions}
                        selected={this.state.format.key}
                        onChange={this.setFormat}
                        className="ai-image-preference-dropdown"
                      />
                    </FormGroup>
                  </div>

                  <div className="col-md-6">
                    <FormGroup>
                      <Label className="ai-image-preference-option-label">Complexity</Label>
                      <DropdownComponent
                        data={RexConstants.ComplexityDropdownOptions}
                        selected={this.state.complexity.key}
                        onChange={this.setComplexity}
                        className="ai-image-preference-dropdown"
                      />
                    </FormGroup>
                  </div>

                  <div className="col-md-6">
                    <FormGroup>
                      <Label className="ai-image-preference-option-label">Perspective</Label>
                      <DropdownComponent
                        data={RexConstants.PerspectiveDropdownOptions}
                        selected={this.state.perspective.key}
                        onChange={this.setPerspective}
                        className="ai-image-preference-dropdown"
                      />
                    </FormGroup>
                  </div>

                  <div className="col-md-6">
                    <FormGroup>
                      <Label className="ai-image-preference-option-label">Creativity</Label>
                      <DropdownComponent
                        data={RexConstants.CreativityDropdownOptions}
                        selected={this.state.creativity.key}
                        onChange={this.setCreativity}
                        className="ai-image-preference-dropdown"
                      />
                    </FormGroup>
                  </div>
                </div>
              </div>

              <div className="ai-image-preview-info mt-3">
                <div className="alert alert-info">
                  <strong>Preview:</strong> {this.generatePreviewText()}
                </div>
              </div>
            </CardBody>
          </Card>

          <FormGroup className="ai-image-preference-default-toggle">
            <div className="form-check mb-2">
              <input
                type="checkbox"
                id="isDefaultTextSwitch"
                checked={this.state.isDefault}
                onChange={this.setIsDefault}
                className="form-check-input"
                style={{ 
                  width: '18px', 
                  height: '18px', 
                  cursor: 'pointer'
                } as React.CSSProperties}
              />
              <label htmlFor="isDefaultTextSwitch" className="form-check-label" style={{ cursor: 'pointer', fontWeight: 500, marginLeft: '8px' }}>
                Set as default preference
              </label>
            </div>
            <small className="form-text text-muted" style={{ marginLeft: '26px' }}>
              The default preference will be automatically selected when generating AI content.
            </small>
          </FormGroup>
        </div>
      </div>
    )
  }
}